"use client";

import { ChevronsRight, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { cn } from "@/lib/utils";
import { Tooltip } from "@/components/common/ui/Tooltip";
import { Typography } from "@/components/common/ui/Typography";

export interface TabbedPanelItem {
  icon: ReactElement;
  component: ReactElement;
  editable?: boolean;
}

export interface TabbedPanelItems {
  [key: string]: TabbedPanelItem;
}

export interface TabbedPanelProps<T extends string = string> {
  items: TabbedPanelItems;
  activeItem?: T;
  secondaryItem?: T | undefined;
  defaultOpen?: boolean;
  storageKey?: string;
  className?: string;
  searcherComponent?: ReactElement;
  onItemClick?: (itemKey: T) => void;
  onActiveItemChange?: (itemKey: T) => void;
  onSecondaryItemChange?: (itemKey?: T | undefined) => void;
  onSidebarToggle?: (isOpen: boolean) => void;
}

interface TabbedPanelState<T extends string = string> {
  isOpen: boolean;
  isSettingSecondaryTab: boolean;
  draggedItem: T | null;
  isHovered: boolean;
}

export function TabbedPanel<T extends string = string>({
  items,
  activeItem,
  secondaryItem,
  defaultOpen = true,
  storageKey = "tabbedPanelOpen",
  className,
  searcherComponent,
  onItemClick,
  onActiveItemChange,
  onSecondaryItemChange,
  onSidebarToggle,
}: TabbedPanelProps<T>) {
  const [state, setState] = useState<TabbedPanelState<T>>(() => {
    const savedOpen =
      typeof window !== "undefined"
        ? localStorage.getItem(storageKey)
        : null;
    const isOpen = savedOpen === "true" || (savedOpen === null && defaultOpen);

    return {
      isOpen,
      isSettingSecondaryTab: false,
      draggedItem: null,
      isHovered: false,
    };
  });

  const sidebarRef = useRef<HTMLDivElement>(null);
  const itemKeys = Object.keys(items) as T[];

  const updateState = (updates: Partial<TabbedPanelState<T>>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const handleItemClick = (itemKey: T) => {
    if (itemKey === secondaryItem) {
      onSecondaryItemChange?.(undefined);
      return;
    }

    if (state.isSettingSecondaryTab) {
      if (itemKey !== activeItem) {
        onSecondaryItemChange?.(itemKey);
      }
      return;
    }

    onActiveItemChange?.(itemKey);
    onItemClick?.(itemKey);
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLElement>,
    itemKey: T,
  ) => {
    updateState({ draggedItem: itemKey, isSettingSecondaryTab: true });
    e.dataTransfer.setData("application/x-tabbed-panel-item", itemKey);
    e.dataTransfer.effectAllowed = "move";

    window.dispatchEvent(
      new CustomEvent("tabbed-panel-drag-start", { detail: { itemKey } }),
    );
  };

  const handleDragEnd = () => {
    updateState({
      draggedItem: null,
      isSettingSecondaryTab: false,
    });

    window.dispatchEvent(new CustomEvent("tabbed-panel-drag-end"));
  };

  const togglePanel = () => {
    const newOpen = !state.isOpen;
    updateState({ isOpen: newOpen });
    onSidebarToggle?.(newOpen);
  };

  // Keyboard shortcuts for secondary tab mode
  useEffect(() => {
    if (!state.isHovered) {
      // Don't reset secondary mode while a drag is in progress —
      // the mouse leaves the narrow collapsed panel immediately when
      // dragging toward the content areas, which would hide the drop zone.
      if (!state.draggedItem) {
        updateState({ isSettingSecondaryTab: false });

        window.dispatchEvent(
          new CustomEvent("tabbed-panel-secondary-mode", {
            detail: { enabled: false },
          }),
        );
      }
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") {
        updateState({ isSettingSecondaryTab: true });
        window.dispatchEvent(
          new CustomEvent("tabbed-panel-secondary-mode", {
            detail: { enabled: true },
          }),
        );
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") {
        updateState({ isSettingSecondaryTab: false });
        window.dispatchEvent(
          new CustomEvent("tabbed-panel-secondary-mode", {
            detail: { enabled: false },
          }),
        );
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [state.isHovered, state.draggedItem]);

  // Safety net: listen for native dragend to ensure state always resets.
  // React's synthetic onDragEnd may not fire if the Tooltip's safePolygon
  // overlay intercepts pointer events during the drag lifecycle.
  useEffect(() => {
    const handleNativeDragEnd = () => {
      setState((prev) => {
        if (!prev.draggedItem) return prev;
        window.dispatchEvent(new CustomEvent("tabbed-panel-drag-end"));
        return {
          ...prev,
          draggedItem: null,
          isSettingSecondaryTab: false,
        };
      });
    };

    window.addEventListener("dragend", handleNativeDragEnd);
    return () => window.removeEventListener("dragend", handleNativeDragEnd);
  }, []);

  // Persist panel open state
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, state.isOpen.toString());
    }
  }, [state.isOpen, storageKey]);

  return (
    <aside
      id="tabbed-panel"
      ref={sidebarRef}
      className={cn(
        "flex flex-col rounded-2xl bg-gray-100 gap-2 p-1 w-12 transition-all duration-300 h-full shadow-sm",
        state.isOpen && "w-[220px] p-2",
        className,
        !state.isOpen && "w-12",
      )}
      onMouseEnter={() => updateState({ isHovered: true })}
      onMouseLeave={() => updateState({ isHovered: false })}
    >
      <div className="flex items-start gap-1 h-14">
        {state.isOpen && searcherComponent && (
          <div id="tabbed-panel-search" className="flex-1">
            {searcherComponent}
          </div>
        )}
        <button
          id="tabbed-panel-toggle"
          className={cn(
            "inline-flex items-center justify-center rounded-full h-10 w-10 hover:bg-gray-200 transition-colors cursor-pointer",
            !searcherComponent && "w-full",
          )}
          onClick={togglePanel}
        >
          <ChevronsRight
            className={cn(
              "transition-transform duration-300",
              state.isOpen && "rotate-180",
            )}
          />
        </button>
      </div>

      {itemKeys.map((itemKey) => {
        const item = items[itemKey];
        if (!item) return null;

        const tabButton = (
          <button
            id={`tabbed-panel-tab-${itemKey}`}
            onClick={() => handleItemClick(itemKey)}
            className={cn(
              "inline-flex items-center h-10 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium cursor-pointer justify-start gap-2 w-full group",
              activeItem === itemKey && "bg-navigator-primary text-white hover:bg-navigator-primary-dark",
              secondaryItem === itemKey &&
                "bg-amber-500 text-white hover:bg-red-500 hover:text-white",
              state.isSettingSecondaryTab &&
                activeItem !== itemKey &&
                secondaryItem !== itemKey &&
                "bg-amber-100 text-amber-700",
              state.draggedItem === itemKey && "opacity-50",
              !state.isOpen && "rounded-full justify-center px-0 w-10",
            )}
          >
            <X
              className={cn(
                "hidden",
                secondaryItem === itemKey && "group-hover:block",
              )}
            />
            <span
              className={cn(
                "transition-colors duration-300",
                secondaryItem === itemKey && "group-hover:hidden",
              )}
            >
              {item.icon}
            </span>
            <Typography
              as="span"
              variant="body"
              color="inherit"
              className={cn(
                "transition-all duration-100 opacity-0 absolute text-left",
                state.isOpen && "opacity-100 static duration-300",
              )}
            >
              {itemKey}
            </Typography>
          </button>
        );

        return (
          <div
            key={itemKey}
            className="w-full z-50"
            draggable
            onDragStart={(e) => handleDragStart(e, itemKey)}
            onDragEnd={handleDragEnd}
          >
            {!state.isOpen && !state.draggedItem ? (
              <Tooltip content={itemKey} placement="right">
                {tabButton}
              </Tooltip>
            ) : (
              tabButton
            )}
          </div>
        );
      })}
    </aside>
  );
}
